import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";

const WellnessContext = createContext();

// Defaults
const DEFAULT_BREAK_INTERVAL = 60; // minutes between breaks
const DEFAULT_WATER_GOAL = 8; // glasses per day
const DEFAULT_WORK_HOURS = 8; // hours of work day
const TICK_INTERVAL = 60000; // 1 minute tick

function getTodayKey() {
  return new Date().toISOString().slice(0, 10); // "2026-02-15"
}

function loadState(userId, key, fallback) {
  try {
    const raw = localStorage.getItem(`wellness_${userId}_${key}`);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    // If it's a daily state, check if it's from today
    if (parsed._day && parsed._day !== getTodayKey()) return fallback;
    return parsed;
  } catch {
    return fallback;
  }
}

function saveState(userId, key, value) {
  try {
    localStorage.setItem(`wellness_${userId}_${key}`, JSON.stringify({ ...value, _day: getTodayKey() }));
  } catch {
    // localStorage full — silently ignore
  }
}

function loadConfig(userId, key, fallback) {
  try {
    const raw = localStorage.getItem(`wellness_${userId}_cfg_${key}`);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function saveConfig(userId, key, value) {
  try {
    localStorage.setItem(`wellness_${userId}_cfg_${key}`, JSON.stringify(value));
  } catch {}
}

export function WellnessProvider({ children }) {
  // Get userId from the closest auth context — we'll pass it as a prop or detect from localStorage
  const [userId, setUserId] = useState(() => {
    // Try to detect user from common patterns
    return localStorage.getItem("wellness_active_user") || "default";
  });

  // ─── Break Timer State ────────────────────────────────────────
  const [breakInterval, setBreakIntervalState] = useState(() => loadConfig(userId, "breakInterval", DEFAULT_BREAK_INTERVAL));
  const [minutesUntilBreak, setMinutesUntilBreak] = useState(() => {
    const saved = loadState(userId, "break", null);
    return saved?.minutesUntilBreak ?? breakInterval;
  });
  const [isOnBreak, setIsOnBreak] = useState(false);
  const [breakStartTime, setBreakStartTime] = useState(null);
  const [breakHistory, setBreakHistory] = useState(() => {
    const saved = loadState(userId, "break", null);
    return saved?.breakHistory ?? [];
  });
  const [breakAlertActive, setBreakAlertActive] = useState(false);

  // ─── Water Tracker State ──────────────────────────────────────
  const [waterGoal, setWaterGoalState] = useState(() => loadConfig(userId, "waterGoal", DEFAULT_WATER_GOAL));
  const [waterConsumed, setWaterConsumed] = useState(() => {
    const saved = loadState(userId, "water", null);
    return saved?.waterConsumed ?? 0;
  });
  const [minutesUntilWater, setMinutesUntilWater] = useState(() => {
    const interval = Math.round((DEFAULT_WORK_HOURS * 60) / loadConfig(userId, "waterGoal", DEFAULT_WATER_GOAL));
    const saved = loadState(userId, "water", null);
    return saved?.minutesUntilWater ?? interval;
  });
  const [waterAlertActive, setWaterAlertActive] = useState(false);

  // ─── Wellness enabled toggle ──────────────────────────────────
  const [wellnessEnabled, setWellnessEnabled] = useState(() => loadConfig(userId, "enabled", true));

  const tickRef = useRef(null);

  // Computed water interval
  const waterIntervalMinutes = Math.round((DEFAULT_WORK_HOURS * 60) / waterGoal);

  // ─── Persistence ──────────────────────────────────────────────
  useEffect(() => {
    saveState(userId, "break", { minutesUntilBreak, breakHistory });
  }, [userId, minutesUntilBreak, breakHistory]);

  useEffect(() => {
    saveState(userId, "water", { waterConsumed, minutesUntilWater });
  }, [userId, waterConsumed, minutesUntilWater]);

  // ─── Tick (every minute) ──────────────────────────────────────
  useEffect(() => {
    if (!wellnessEnabled) return;

    tickRef.current = setInterval(() => {
      // Break countdown
      setMinutesUntilBreak((prev) => {
        if (prev <= 1) {
          setBreakAlertActive(true);
          toast.warning("Hora de descansar! Llevas mucho tiempo trabajando.", {
            duration: 10000,
            action: { label: "Descansar", onClick: () => startBreak() },
          });
          return 0;
        }
        return prev - 1;
      });

      // Water countdown (only if goal not met)
      setWaterConsumed((currentConsumed) => {
        setWaterGoalState((currentGoal) => {
          if (currentConsumed < currentGoal) {
            setMinutesUntilWater((prev) => {
              if (prev <= 1) {
                setWaterAlertActive(true);
                toast.info(`Hora de tomar agua! Vaso ${currentConsumed + 1}/${currentGoal}`, {
                  duration: 8000,
                  action: { label: "Listo", onClick: () => drinkWater() },
                });
                return waterIntervalMinutes;
              }
              return prev - 1;
            });
          }
          return currentGoal;
        });
        return currentConsumed;
      });
    }, TICK_INTERVAL);

    return () => clearInterval(tickRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wellnessEnabled, waterIntervalMinutes]);

  // ─── Break Actions ────────────────────────────────────────────
  const startBreak = useCallback(() => {
    setIsOnBreak(true);
    setBreakStartTime(Date.now());
    setBreakAlertActive(false);
    toast.success("Descanso iniciado. Relax!");
  }, []);

  const endBreak = useCallback(() => {
    if (breakStartTime) {
      const duration = Math.round((Date.now() - breakStartTime) / 60000);
      setBreakHistory((prev) => [
        ...prev,
        { start: new Date(breakStartTime).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" }), duration },
      ]);
    }
    setIsOnBreak(false);
    setBreakStartTime(null);
    setMinutesUntilBreak(breakInterval);
    toast.success("Buen descanso! De vuelta al trabajo.");
  }, [breakStartTime, breakInterval]);

  const postponeBreak = useCallback((minutes = 15) => {
    setMinutesUntilBreak(minutes);
    setBreakAlertActive(false);
    toast("Descanso pospuesto " + minutes + " minutos");
  }, []);

  const setBreakInterval = useCallback((minutes) => {
    setBreakIntervalState(minutes);
    setMinutesUntilBreak(minutes);
    saveConfig(userId, "breakInterval", minutes);
  }, [userId]);

  // ─── Water Actions ────────────────────────────────────────────
  const drinkWater = useCallback(() => {
    setWaterConsumed((prev) => {
      const next = prev + 1;
      if (next >= waterGoal) {
        toast.success("Meta de hidratacion cumplida! Excelente!");
      } else {
        toast.success(`Vaso ${next}/${waterGoal} registrado`);
      }
      return next;
    });
    setMinutesUntilWater(waterIntervalMinutes);
    setWaterAlertActive(false);
  }, [waterGoal, waterIntervalMinutes]);

  const setWaterGoal = useCallback((goal) => {
    setWaterGoalState(goal);
    saveConfig(userId, "waterGoal", goal);
    const newInterval = Math.round((DEFAULT_WORK_HOURS * 60) / goal);
    setMinutesUntilWater(newInterval);
  }, [userId]);

  // ─── Toggle wellness ──────────────────────────────────────────
  const toggleWellness = useCallback(() => {
    setWellnessEnabled((prev) => {
      const next = !prev;
      saveConfig(userId, "enabled", next);
      return next;
    });
  }, [userId]);

  // ─── Set active user ──────────────────────────────────────────
  const setActiveUser = useCallback((id) => {
    localStorage.setItem("wellness_active_user", id);
    setUserId(id);
  }, []);

  // Whether any alert is actively pulsing
  const hasActiveAlert = breakAlertActive || waterAlertActive;

  return (
    <WellnessContext.Provider
      value={{
        // State
        wellnessEnabled,
        breakInterval,
        minutesUntilBreak,
        isOnBreak,
        breakStartTime,
        breakHistory,
        breakAlertActive,
        waterGoal,
        waterConsumed,
        minutesUntilWater,
        waterIntervalMinutes,
        waterAlertActive,
        hasActiveAlert,
        // Actions
        startBreak,
        endBreak,
        postponeBreak,
        setBreakInterval,
        drinkWater,
        setWaterGoal,
        toggleWellness,
        setActiveUser,
      }}
    >
      {children}
    </WellnessContext.Provider>
  );
}

export function useWellness() {
  const context = useContext(WellnessContext);
  if (!context) {
    throw new Error("useWellness must be used within a WellnessProvider");
  }
  return context;
}
